import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  createGlobalSupplier,
  loadCaseSourcingDocument,
  resolveGlobalSupplier,
  saveCaseSourcingDocument,
  searchGlobalSuppliers,
  type CaseSourcingDocument,
  type CaseSourcingDocumentResponse,
  type GlobalSupplierChoice,
  type SourcingHistoryCandidate,
  type SourcingPriceTier,
  type SourcingProduct,
  type SourcingSupplierOption,
} from "./api";

export function normalizeSupplierDomainInput(value: string) {
  return value
    .normalize("NFKC")
    .trim()
    .toLocaleLowerCase("en-US")
    .replace(/^www\./, "")
    .replace(/\.$/, "");
}

function SupplierDirectoryPicker({
  value,
  disabled,
  onSelect,
}: {
  value: string;
  disabled: boolean;
  onSelect: (choice: GlobalSupplierChoice) => Promise<void>;
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [options, setOptions] = useState<GlobalSupplierChoice[]>([]);
  const [error, setError] = useState("");

  useEffect(() => setQuery(value), [value]);
  useEffect(() => {
    if (!open || disabled) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError("");
      void searchGlobalSuppliers(query, controller.signal)
        .then(setOptions)
        .catch((reason: unknown) => {
          if (!controller.signal.aborted) {
            setOptions([]);
            setError(reason instanceof Error ? reason.message : String(reason));
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 150);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [disabled, open, query]);

  async function choose(choice: GlobalSupplierChoice) {
    setChoosing(true);
    setError("");
    try {
      await onSelect(choice);
      setQuery(choice.displayName);
      setOpen(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setChoosing(false);
    }
  }

  return (
    <div className="case-sourcing-supplier-picker">
      <span>供应商</span>
      <input
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        value={query}
        disabled={disabled || choosing}
        placeholder="输入供应商名称或域名"
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
      />
      {open && (
        <div className="case-sourcing-supplier-options" role="listbox">
          {loading && <small>正在搜索…</small>}
          {!loading && !options.length && !error && (
            <small>没有匹配的供应商</small>
          )}
          {error && (
            <small className="case-sourcing-picker-error">{error}</small>
          )}
          {options.map((choice) => (
            <button
              type="button"
              role="option"
              key={choice.identityId}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => void choose(choice)}
            >
              <strong>{choice.displayName}</strong>
              <span>{choice.domain ?? "未登记域名"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
const blankTier = (): SourcingPriceTier => ({
  purity: "",
  leadTime: "",
  hasInventory: false,
  specUnit: "g",
  specValue: "",
  priceValue: "",
  currency: "USD",
  validUntil: "",
});
const blankSupplier = (): SourcingSupplierOption => ({
  supplierId: null,
  supplierName: "",
  shippingConditions: "",
  shippingForm: "solid",
  isAlternative: false,
  isBestSupplier: false,
  isOverseasSupplier: false,
  remarks: "",
  priceTiers: [blankTier()],
});
const blankProduct = (): SourcingProduct => ({
  key: crypto.randomUUID(),
  sourceInboundInquiryId: null,
  productName: "",
  casNo: "",
  catalogNumber: "",
  requestedQuantity: "",
  specification: "",
  outcome: "PENDING",
  noSupplyReason: "",
  clarificationNotes: "",
  sourceType: "MANUAL",
  reusedFromSourcingResultId: null,
  supplierOptions: [],
});
export function appendBlankSupplier(
  product: SourcingProduct,
): Pick<SourcingProduct, "outcome" | "supplierOptions"> {
  return {
    outcome: "FOUND",
    supplierOptions: [...product.supplierOptions, blankSupplier()],
  };
}
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const statusName: Record<string, string> = {
  DRAFT: "待正式保存",
  SUBMITTED: "正式版本",
  SUPERSEDED: "历史版本",
};

export function buildSourcingPayload(source: CaseSourcingDocumentResponse) {
  return {
    ...(source.document.id ? { documentId: source.document.id } : {}),
    ...(source.document.id
      ? { expectedLockVersion: source.document.lockVersion }
      : {}),
    sourceComment: source.document.sourceComment,
    reason: source.document.id ? "保存查货单修改" : "从案件询单创建查货单",
    products: source.document.products.map(({ key: _key, ...product }) => ({
      ...product,
      casNo: product.casNo ?? "",
      catalogNumber: product.catalogNumber ?? "",
      requestedQuantity: product.requestedQuantity ?? "",
      specification: product.specification ?? "",
      noSupplyReason: product.noSupplyReason ?? "",
      clarificationNotes: product.clarificationNotes ?? "",
      supplierOptions: product.supplierOptions.map((option) => ({
        ...option,
        shippingConditions: option.shippingConditions ?? "",
        shippingForm: option.shippingForm ?? "",
        remarks: option.remarks ?? "",
        priceTiers: option.priceTiers.map((tier) => ({
          ...tier,
          purity: tier.purity ?? "",
          leadTime: tier.leadTime ?? "",
          priceValue: tier.priceValue ?? "",
        })),
      })),
    })),
  };
}

export function CaseSourcingPanel({
  caseId,
  disabled,
  canManage,
  onChanged,
}: {
  caseId: string;
  disabled: boolean;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [data, setData] = useState<CaseSourcingDocumentResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [supplierCreateTarget, setSupplierCreateTarget] = useState<{
    productIndex: number;
    supplierIndex: number;
  } | null>(null);
  const [supplierCreateName, setSupplierCreateName] = useState("");
  const [supplierCreateDomain, setSupplierCreateDomain] = useState("");
  const [supplierCreateError, setSupplierCreateError] = useState("");
  const [supplierCreating, setSupplierCreating] = useState(false);

  async function reload(signal?: AbortSignal) {
    setLoading(true);
    setError("");
    try {
      const response = await loadCaseSourcingDocument(caseId, signal);
      setData(response);
      setDirty(false);
      return response;
    } catch (reason) {
      if (!signal?.aborted)
        setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
    return null;
  }

  useEffect(() => {
    const controller = new AbortController();
    void reload(controller.signal);
    return () => controller.abort();
  }, [caseId]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const productSummary = useMemo(
    () =>
      data?.document.products.map((product) => {
        const best =
          product.supplierOptions.find((option) => option.isBestSupplier) ??
          product.supplierOptions[0];
        return { product, best, tier: best?.priceTiers[0] };
      }) ?? [],
    [data],
  );

  function updateProduct(index: number, patch: Partial<SourcingProduct>) {
    setData((current) => {
      if (!current) return current;
      const next = clone(current);
      next.document.products[index] = {
        ...next.document.products[index]!,
        ...patch,
      };
      return next;
    });
    setDirty(true);
  }

  function updateSupplier(
    productIndex: number,
    supplierIndex: number,
    patch: Partial<SourcingSupplierOption>,
  ) {
    setData((current) => {
      if (!current) return current;
      const next = clone(current);
      next.document.products[productIndex]!.supplierOptions[supplierIndex] = {
        ...next.document.products[productIndex]!.supplierOptions[
          supplierIndex
        ]!,
        ...patch,
      };
      return next;
    });
    setDirty(true);
  }

  function updateTier(
    productIndex: number,
    supplierIndex: number,
    tierIndex: number,
    patch: Partial<SourcingPriceTier>,
  ) {
    setData((current) => {
      if (!current) return current;
      const next = clone(current);
      const tier =
        next.document.products[productIndex]!.supplierOptions[supplierIndex]!
          .priceTiers[tierIndex]!;
      next.document.products[productIndex]!.supplierOptions[
        supplierIndex
      ]!.priceTiers[tierIndex] = { ...tier, ...patch };
      return next;
    });
    setDirty(true);
  }

  async function selectSupplier(
    productIndex: number,
    supplierIndex: number,
    choice: GlobalSupplierChoice,
  ) {
    const selected = choice.supplierId
      ? choice
      : await resolveGlobalSupplier(choice.identityId);
    updateSupplier(productIndex, supplierIndex, {
      supplierId: selected.supplierId,
      supplierName: selected.displayName,
    });
  }

  function openSupplierCreate(productIndex: number, supplierIndex: number) {
    setSupplierCreateTarget({ productIndex, supplierIndex });
    setSupplierCreateName("");
    setSupplierCreateDomain("");
    setSupplierCreateError("");
  }

  async function submitSupplierCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!supplierCreateTarget) return;
    const domain = normalizeSupplierDomainInput(supplierCreateDomain);
    setSupplierCreating(true);
    setSupplierCreateError("");
    try {
      const supplier = await createGlobalSupplier({
        name: supplierCreateName,
        ...(domain ? { domain } : {}),
      });
      updateSupplier(
        supplierCreateTarget.productIndex,
        supplierCreateTarget.supplierIndex,
        {
          supplierId: supplier.supplierId,
          supplierName: supplier.displayName,
        },
      );
      setSupplierCreateTarget(null);
    } catch (reason) {
      setSupplierCreateError(
        reason instanceof Error ? reason.message : String(reason),
      );
    } finally {
      setSupplierCreating(false);
    }
  }

  function applyHistory(
    productIndex: number,
    candidate: SourcingHistoryCandidate,
  ) {
    const option: SourcingSupplierOption = {
      supplierId: candidate.supplierId,
      supplierName: candidate.supplierName,
      shippingConditions: "",
      shippingForm: "solid",
      isAlternative: false,
      isBestSupplier: true,
      isOverseasSupplier: false,
      remarks: `引用自 ${candidate.sourceCaseNumber} · ${new Date(candidate.submittedAt).toLocaleDateString("zh-CN")}`,
      priceTiers: [{ ...candidate.priceTier }],
    };
    setData((current) => {
      if (!current) return current;
      const next = clone(current);
      const product = next.document.products[productIndex]!;
      product.outcome = "FOUND";
      product.sourceType = "HISTORY_REUSE";
      product.reusedFromSourcingResultId = candidate.id;
      product.supplierOptions = [option];
      return next;
    });
    setDirty(true);
    setEditing(true);
  }

  async function save() {
    if (!data) return null;
    setSaving(true);
    setError("");
    try {
      const response = await saveCaseSourcingDocument(
        caseId,
        buildSourcingPayload(data),
      );
      setData(response);
      setDirty(false);
      setEditing(false);
      onChanged();
      return response;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
      return null;
    } finally {
      setSaving(false);
    }
  }

  function viewVersion(version: CaseSourcingDocument) {
    if (dirty && !window.confirm("存在未保存修改，确认切换版本？")) return;
    setData((current) =>
      current ? { ...current, document: clone(version) } : current,
    );
    setEditing(false);
    setDirty(false);
  }

  function close() {
    if (dirty && !window.confirm("存在未保存修改，确认放弃并关闭？")) return;
    setOpen(false);
    setEditing(false);
    setDirty(false);
    void reload();
  }

  const latestVersion = data?.versions[0] ?? data?.document;
  const viewingLatest = Boolean(
    data &&
      (!data.document.id ||
        !latestVersion?.id ||
        data.document.id === latestVersion.id),
  );

  return (
    <section className="case-sourcing-panel">
      <h2>查货单</h2>
      {loading && <small>正在读取查货结果…</small>}
      {error && !open && <p className="case-sourcing-error">{error}</p>}
      {data && (
        <>
          <div className="case-sourcing-summary">
            <strong>
              v{data.document.version} · {statusName[data.document.status]}
            </strong>
            <small>
              {data.summary.total} 个产品 · {data.summary.found} 个已找到 ·{" "}
              {data.summary.noSupply} 个无货
              {data.summary.needsClarification
                ? ` · ${data.summary.needsClarification} 个待澄清`
                : ""}
            </small>
            {productSummary.slice(0, 4).map(({ product, best, tier }) => (
              <p key={product.key}>
                <b>{product.productName}</b>：{" "}
                {product.outcome === "NO_SUPPLY"
                  ? `无货 · ${product.noSupplyReason || "未填写原因"}`
                  : product.outcome === "NEEDS_CLARIFICATION"
                    ? "待澄清"
                    : best
                      ? `${best.supplierName}${tier ? ` · ${tier.currency} ${tier.priceValue ?? "成本受限"}/${tier.specValue}${tier.specUnit}` : ""}${tier?.leadTime ? ` · ${tier.leadTime}` : ""}`
                      : "尚未填写结果"}
              </p>
            ))}
          </div>
          <button
            className="case-primary"
            disabled={disabled || (!canManage && !data.exists)}
            onClick={() => {
              setOpen(true);
              setEditing(!data.exists && data.canEdit);
            }}
          >
            {data.exists ? "管理查货单" : "新建查货单"}
          </button>
        </>
      )}
      {open && data && (
        <div className="case-sourcing-overlay" role="presentation">
          <div
            className="case-sourcing-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="case-sourcing-title"
          >
            <header>
              <div>
                <h2 id="case-sourcing-title" className="case-document-version-title">
                  <span>查货单</span>
                  <select
                    aria-label="查货单版本"
                    value={data.document.id ?? "initial"}
                    onChange={(event) => {
                      const version = data.versions.find(
                        (item) => item.id === event.target.value,
                      );
                      if (version) viewVersion(version);
                    }}
                  >
                    {!data.document.id && <option value="initial">v1</option>}
                    {data.versions.map((version) => (
                      <option key={version.id} value={version.id ?? ""}>
                        v{version.version} · {statusName[version.status]}
                      </option>
                    ))}
                  </select>
                </h2>
                <p>维护产品、供应商方案和规格价格档。</p>
              </div>
              <div className="case-sourcing-mode">
                <button
                  aria-pressed={!editing}
                  onClick={() => {
                    if (
                      !dirty ||
                      window.confirm("放弃未保存修改并返回预览？")
                    ) {
                      setEditing(false);
                      setDirty(false);
                      void reload();
                    }
                  }}
                >
                  预览
                </button>
                <button
                  aria-pressed={editing}
                  disabled={!data.canEdit || disabled || !viewingLatest}
                  onClick={() => setEditing(true)}
                >
                  编辑
                </button>
                <button aria-label="关闭查货单" onClick={close}>
                  ×
                </button>
              </div>
            </header>
            <main>
              {error && <div className="case-sourcing-error">{error}</div>}
              {data.document.sourceComment && (
                <aside className="case-sourcing-comment">
                  <strong>询单备注</strong>
                  <p>{data.document.sourceComment}</p>
                </aside>
              )}
              {data.document.products.map((product, productIndex) => {
                const candidates = data.historyCandidates[product.key] ?? [];
                return (
                  <article className="case-sourcing-product" key={product.key}>
                    <div className="case-sourcing-product-heading">
                      <h3>产品 {productIndex + 1}</h3>
                      {editing && data.document.products.length > 1 && (
                        <button
                          onClick={() => {
                            setData((current) => {
                              if (!current) return current;
                              const next = clone(current);
                              next.document.products.splice(productIndex, 1);
                              return next;
                            });
                            setDirty(true);
                          }}
                        >
                          删除产品
                        </button>
                      )}
                    </div>
                    <div className="case-sourcing-grid">
                      <label>
                        产品名称
                        <input
                          value={product.productName}
                          readOnly={!editing}
                          onChange={(event) =>
                            updateProduct(productIndex, {
                              productName: event.target.value,
                            })
                          }
                        />
                      </label>
                      <label>
                        CAS
                        <input
                          value={product.casNo ?? ""}
                          readOnly={!editing}
                          onChange={(event) =>
                            updateProduct(productIndex, {
                              casNo: event.target.value,
                            })
                          }
                        />
                      </label>
                      <label>
                        Cat No
                        <input
                          value={product.catalogNumber ?? ""}
                          readOnly={!editing}
                          onChange={(event) =>
                            updateProduct(productIndex, {
                              catalogNumber: event.target.value,
                            })
                          }
                        />
                      </label>
                      <label>
                        需求数量
                        <input
                          value={product.requestedQuantity ?? ""}
                          readOnly={!editing}
                          onChange={(event) =>
                            updateProduct(productIndex, {
                              requestedQuantity: event.target.value,
                            })
                          }
                        />
                      </label>
                      <label className="case-sourcing-wide">
                        规格说明
                        <input
                          value={product.specification ?? ""}
                          readOnly={!editing}
                          onChange={(event) =>
                            updateProduct(productIndex, {
                              specification: event.target.value,
                            })
                          }
                        />
                      </label>
                      <label>
                        查货结果
                        <select
                          value={product.outcome}
                          disabled={!editing}
                          onChange={(event) =>
                            updateProduct(productIndex, {
                              outcome: event.target
                                .value as SourcingProduct["outcome"],
                            })
                          }
                        >
                          <option value="PENDING">待处理</option>
                          <option value="FOUND">已找到</option>
                          <option value="NO_SUPPLY">无货</option>
                          <option value="NEEDS_CLARIFICATION">待澄清</option>
                        </select>
                      </label>
                    </div>
                    {product.outcome === "NO_SUPPLY" && (
                      <label>
                        无货原因
                        <textarea
                          value={product.noSupplyReason ?? ""}
                          readOnly={!editing}
                          onChange={(event) =>
                            updateProduct(productIndex, {
                              noSupplyReason: event.target.value,
                            })
                          }
                        />
                      </label>
                    )}
                    {product.outcome === "NEEDS_CLARIFICATION" && (
                      <label>
                        待澄清内容
                        <textarea
                          value={product.clarificationNotes ?? ""}
                          readOnly={!editing}
                          onChange={(event) =>
                            updateProduct(productIndex, {
                              clarificationNotes: event.target.value,
                            })
                          }
                        />
                      </label>
                    )}
                    {candidates.length > 0 && (
                      <details className="case-sourcing-history">
                        <summary>近期可复用结果（{candidates.length}）</summary>
                        {candidates.map((candidate) => (
                          <div key={candidate.id}>
                            <span>
                              {candidate.sourceCaseNumber} ·{" "}
                              {candidate.supplierName} ·{" "}
                              {candidate.priceTier.currency}{" "}
                              {candidate.priceTier.priceValue ?? "成本受限"} ·
                              有效至{" "}
                              {candidate.priceTier.validUntil || "未记录"}
                            </span>
                            {editing && (
                              <button
                                onClick={() =>
                                  applyHistory(productIndex, candidate)
                                }
                              >
                                引用近期结果
                              </button>
                            )}
                          </div>
                        ))}
                      </details>
                    )}
                    {product.supplierOptions.map((option, supplierIndex) => (
                      <section
                        className="case-sourcing-supplier"
                        key={
                          option.id ??
                          `${product.key}-supplier-${supplierIndex}`
                        }
                      >
                        <div className="case-sourcing-product-heading">
                          <h4>供应商方案 {supplierIndex + 1}</h4>
                          {editing && (
                            <button
                              onClick={() => {
                                const options = [...product.supplierOptions];
                                options.splice(supplierIndex, 1);
                                updateProduct(productIndex, {
                                  supplierOptions: options,
                                });
                              }}
                            >
                              删除供应商
                            </button>
                          )}
                        </div>
                        <div className="case-sourcing-grid">
                          <div className="case-sourcing-supplier-picker-row case-sourcing-wide">
                            <SupplierDirectoryPicker
                              value={option.supplierName}
                              disabled={!editing}
                              onSelect={(choice) =>
                                selectSupplier(
                                  productIndex,
                                  supplierIndex,
                                  choice,
                                )
                              }
                            />
                            {editing && (
                              <button
                                type="button"
                                onClick={() =>
                                  openSupplierCreate(
                                    productIndex,
                                    supplierIndex,
                                  )
                                }
                              >
                                新增供应商
                              </button>
                            )}
                          </div>
                          <label>
                            运输形态
                            <select
                              value={option.shippingForm ?? ""}
                              disabled={!editing}
                              onChange={(event) =>
                                updateSupplier(productIndex, supplierIndex, {
                                  shippingForm: event.target.value,
                                })
                              }
                            >
                              <option value="">未选择</option>
                              <option value="solid">固体</option>
                              <option value="liquid">液体</option>
                              <option value="gas">气体</option>
                            </select>
                          </label>
                          <label>
                            运输条件
                            <input
                              value={option.shippingConditions ?? ""}
                              readOnly={!editing}
                              onChange={(event) =>
                                updateSupplier(productIndex, supplierIndex, {
                                  shippingConditions: event.target.value,
                                })
                              }
                            />
                          </label>
                        </div>
                        <div className="case-sourcing-flags">
                          {[
                            ["isBestSupplier", "最优供应商"],
                            ["isOverseasSupplier", "海外供应商"],
                            ["isAlternative", "替代方案"],
                          ].map(([field, label]) => (
                            <label key={field}>
                              <input
                                type="checkbox"
                                disabled={!editing}
                                checked={Boolean(
                                  option[field as keyof SourcingSupplierOption],
                                )}
                                onChange={(event) =>
                                  updateSupplier(productIndex, supplierIndex, {
                                    [field]: event.target.checked,
                                  })
                                }
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                        {option.priceTiers.map((tier, tierIndex) => (
                          <div
                            className="case-sourcing-tier-scroll"
                            key={tier.id ?? `tier-${tierIndex}`}
                          >
                            <div className="case-sourcing-tier">
                              <div className="case-sourcing-tier-name">
                                <span>价格档</span>
                                <strong>{tierIndex + 1}</strong>
                              </div>
                              <label>
                                <span>纯度</span>
                                <input
                                  placeholder="例如 98%"
                                  value={tier.purity ?? ""}
                                  readOnly={!editing}
                                  onChange={(event) =>
                                    updateTier(
                                      productIndex,
                                      supplierIndex,
                                      tierIndex,
                                      { purity: event.target.value },
                                    )
                                  }
                                />
                              </label>
                              <label>
                                <span>交期</span>
                                <input
                                  placeholder="例如 7 天"
                                  value={tier.leadTime ?? ""}
                                  readOnly={!editing}
                                  onChange={(event) =>
                                    updateTier(
                                      productIndex,
                                      supplierIndex,
                                      tierIndex,
                                      { leadTime: event.target.value },
                                    )
                                  }
                                />
                              </label>
                              <label>
                                <span>规格数值</span>
                                <input
                                  placeholder="例如 1"
                                  value={tier.specValue}
                                  readOnly={!editing}
                                  onChange={(event) =>
                                    updateTier(
                                      productIndex,
                                      supplierIndex,
                                      tierIndex,
                                      { specValue: event.target.value },
                                    )
                                  }
                                />
                              </label>
                              <label>
                                <span>规格单位</span>
                                <select
                                  value={tier.specUnit}
                                  disabled={!editing}
                                  onChange={(event) =>
                                    updateTier(
                                      productIndex,
                                      supplierIndex,
                                      tierIndex,
                                      { specUnit: event.target.value },
                                    )
                                  }
                                >
                                  {[
                                    "ug",
                                    "mg",
                                    "g",
                                    "kg",
                                    "t",
                                    "uL",
                                    "mL",
                                    "L",
                                    "other",
                                  ].map((unit) => (
                                    <option key={unit}>{unit}</option>
                                  ))}
                                </select>
                              </label>
                              <label>
                                <span>价格</span>
                                <input
                                  placeholder="输入价格"
                                  type="number"
                                  min="0"
                                  step="0.0001"
                                  value={tier.priceValue ?? ""}
                                  readOnly={!editing}
                                  onChange={(event) =>
                                    updateTier(
                                      productIndex,
                                      supplierIndex,
                                      tierIndex,
                                      { priceValue: event.target.value },
                                    )
                                  }
                                />
                              </label>
                              <label>
                                <span>币种</span>
                                <select
                                  value={tier.currency}
                                  disabled={!editing}
                                  onChange={(event) =>
                                    updateTier(
                                      productIndex,
                                      supplierIndex,
                                      tierIndex,
                                      { currency: event.target.value },
                                    )
                                  }
                                >
                                  {[
                                    "CNY",
                                    "USD",
                                    "EUR",
                                    "JPY",
                                    "GBP",
                                    "AUD",
                                    "CAD",
                                    "CHF",
                                    "RUB",
                                    "KRW",
                                    "INR",
                                    "IDR",
                                    "PHP",
                                    "SGD",
                                    "MYR",
                                    "THB",
                                    "VND",
                                    "other",
                                  ].map((currency) => (
                                    <option key={currency}>{currency}</option>
                                  ))}
                                </select>
                              </label>
                              <label>
                                <span>有效期</span>
                                <input
                                  type="date"
                                  value={tier.validUntil}
                                  readOnly={!editing}
                                  onChange={(event) =>
                                    updateTier(
                                      productIndex,
                                      supplierIndex,
                                      tierIndex,
                                      { validUntil: event.target.value },
                                    )
                                  }
                                />
                              </label>
                              <label className="case-sourcing-tier-stock">
                                <span>库存</span>
                                <span className="case-sourcing-tier-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={tier.hasInventory}
                                    disabled={!editing}
                                    onChange={(event) =>
                                      updateTier(
                                        productIndex,
                                        supplierIndex,
                                        tierIndex,
                                        { hasInventory: event.target.checked },
                                      )
                                    }
                                  />
                                  有库存
                                </span>
                              </label>
                              <div className="case-sourcing-tier-action">
                                <span>操作</span>
                                {editing && option.priceTiers.length > 1 ? (
                                  <button
                                    onClick={() => {
                                      const tiers = [...option.priceTiers];
                                      tiers.splice(tierIndex, 1);
                                      updateSupplier(
                                        productIndex,
                                        supplierIndex,
                                        { priceTiers: tiers },
                                      );
                                    }}
                                  >
                                    删除
                                  </button>
                                ) : (
                                  <small>—</small>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        {editing && (
                          <button
                            onClick={() =>
                              updateSupplier(productIndex, supplierIndex, {
                                priceTiers: [...option.priceTiers, blankTier()],
                              })
                            }
                          >
                            + 新增价格档
                          </button>
                        )}
                        <label>
                          备注
                          <textarea
                            value={option.remarks ?? ""}
                            readOnly={!editing}
                            onChange={(event) =>
                              updateSupplier(productIndex, supplierIndex, {
                                remarks: event.target.value,
                              })
                            }
                          />
                        </label>
                      </section>
                    ))}
                    {editing && (
                      <div className="case-sourcing-add-supplier">
                        <button
                          onClick={() =>
                            updateProduct(
                              productIndex,
                              appendBlankSupplier(product),
                            )
                          }
                        >
                          + 新增供应商方案
                        </button>
                        <small>
                          新建方案后，可按供应商名称或域名搜索，也可直接新增全局供应商。
                        </small>
                      </div>
                    )}
                  </article>
                );
              })}
              {editing && (
                <button
                  onClick={() =>
                    setData((current) => {
                      if (!current) return current;
                      const next = clone(current);
                      next.document.products.push(blankProduct());
                      setDirty(true);
                      return next;
                    })
                  }
                >
                  + 新增产品
                </button>
              )}
            </main>
            <footer>
              <button onClick={close}>取消</button>
              {editing && (
                <button disabled={saving} onClick={() => void save()}>
                  {saving ? "保存中…" : "保存正式版本"}
                </button>
              )}
            </footer>
          </div>
          {supplierCreateTarget && (
            <div className="case-sourcing-create-overlay" role="presentation">
              <form
                className="case-sourcing-create-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="case-supplier-create-title"
                onSubmit={(event) => void submitSupplierCreate(event)}
              >
                <header>
                  <div>
                    <h3 id="case-supplier-create-title">新增供应商</h3>
                    <p>新供应商将加入全局目录，所有人和所有空间均可搜索。</p>
                  </div>
                  <button
                    type="button"
                    aria-label="关闭新增供应商"
                    onClick={() => setSupplierCreateTarget(null)}
                  >
                    ×
                  </button>
                </header>
                <main>
                  {supplierCreateError && (
                    <div className="case-sourcing-error">
                      {supplierCreateError}
                    </div>
                  )}
                  <label>
                    供应商名称
                    <input
                      autoFocus
                      required
                      maxLength={240}
                      value={supplierCreateName}
                      onChange={(event) =>
                        setSupplierCreateName(event.target.value)
                      }
                    />
                  </label>
                  <label>
                    供应商域名（选填）
                    <input
                      maxLength={253}
                      value={supplierCreateDomain}
                      placeholder="例如 supplier.com"
                      onChange={(event) =>
                        setSupplierCreateDomain(event.target.value)
                      }
                      onBlur={() =>
                        setSupplierCreateDomain((current) =>
                          normalizeSupplierDomainInput(current),
                        )
                      }
                    />
                  </label>
                  <small>
                    没有域名可以留空；填写时只写域名，不要填写超链接。开头的
                    www. 会自动去除，并统一转为小写。
                  </small>
                </main>
                <footer>
                  <button
                    type="button"
                    disabled={supplierCreating}
                    onClick={() => setSupplierCreateTarget(null)}
                  >
                    取消
                  </button>
                  <button
                    className="case-primary"
                    disabled={supplierCreating}
                    type="submit"
                  >
                    {supplierCreating ? "新增中…" : "确认新增"}
                  </button>
                </footer>
              </form>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
